import React, { useState, useEffect } from "react";
import {
  Modal,
  Stack,
  Text,
  IconButton,
  TextField,
  MessageBar,
  MessageBarType,
  Spinner,
  SpinnerSize,
} from "@fluentui/react";

interface Props {
  isOpen: boolean;
  fileName?: string;
  secureLink?: string;
  loading?: boolean;
  error?: string;
  onDismiss: () => void;
}

const SecureLinkModal = (props: Props) => {
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  useEffect(() => {
    if (copySuccess) {
      const timer = setTimeout(() => {
        setCopySuccess(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [copySuccess]);

  const handleCopyToClipboard = async () => {
    if (props.secureLink) {
      try {
        await navigator.clipboard.writeText(props.secureLink);
        setCopySuccess(true);
      } catch (error) {
        console.error("Failed to copy to clipboard:", error);
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = props.secureLink;
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand("copy");
          setCopySuccess(true);
        } catch (fallbackError) {
          console.error("Fallback copy failed:", fallbackError);
        }
        document.body.removeChild(textArea);
      }
    }
  };

  return (
    <Modal
      isOpen={props.isOpen}
      onDismiss={props.onDismiss}
      isBlocking={false}
      containerClassName="secure-link-modal"
    >
      <div style={{ padding: "24px", minWidth: "400px", maxWidth: "600px" }}>
        <Stack tokens={{ childrenGap: 16 }}>
          <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
            <Text variant="xLarge" style={{ fontWeight: "600" }}>
              Secure Link
            </Text>
            <IconButton
              iconProps={{ iconName: "Cancel" }}
              ariaLabel="Close modal"
              onClick={props.onDismiss}
              styles={{
                root: {
                  color: "#605e5c",
                  fontSize: "16px",
                },
                rootHovered: {
                  backgroundColor: "#f3f2f1",
                  color: "#323130",
                },
              }}
            />
          </Stack>

          {props.fileName && (
            <Text variant="medium" style={{ color: "#605e5c" }}>
              File: <strong>{props.fileName}</strong>
            </Text>
          )}

          {props.loading && (
            <Stack horizontal horizontalAlign="center" tokens={{ childrenGap: 8 }}>
              <Spinner size={SpinnerSize.small} />
              <Text variant="medium">Generating secure link...</Text>
            </Stack>
          )}

          {props.error && (
            <MessageBar messageBarType={MessageBarType.error}>
              {props.error}
            </MessageBar>
          )}

          {props.secureLink && !props.loading && (
            <>
              <Stack tokens={{ childrenGap: 8 }}>
                <Text variant="medium" style={{ fontWeight: "600" }}>
                  Secure Link:
                </Text>
                <Stack horizontal tokens={{ childrenGap: 8 }}>
                  <TextField
                    value={props.secureLink}
                    readOnly
                    multiline
                    resizable={false}
                    styles={{
                      root: { flexGrow: 1 },
                      field: {
                        backgroundColor: "#f8f8f8",
                        border: "1px solid #d1d1d1",
                        fontSize: "12px",
                        fontFamily: "Monaco, Consolas, 'Courier New', monospace",
                      },
                    }}
                  />
                  <IconButton
                    iconProps={{ iconName: "Copy" }}
                    title="Copy to clipboard"
                    ariaLabel="Copy secure link to clipboard"
                    onClick={handleCopyToClipboard}
                    styles={{
                      root: {
                        width: "40px",
                        height: "40px",
                        backgroundColor: "#0078d4",
                        color: "white",
                        border: "none",
                        alignSelf: "flex-start",
                        marginTop: "4px",
                      },
                      rootHovered: {
                        backgroundColor: "#106ebe",
                      },
                      rootPressed: {
                        backgroundColor: "#005a9e",
                      },
                      icon: {
                        fontSize: "16px",
                      },
                    }}
                  />
                </Stack>
              </Stack>

              {copySuccess && (
                <MessageBar
                  messageBarType={MessageBarType.success}
                  styles={{
                    root: {
                      marginTop: "8px",
                    },
                  }}
                >
                  Secure link copied to clipboard!
                </MessageBar>
              )}

              <Text variant="small" style={{ color: "#605e5c", fontStyle: "italic" }}>
                This secure link will expire after a certain period. Please use it promptly.
              </Text>
            </>
          )}
        </Stack>
      </div>
    </Modal>
  );
};

export default SecureLinkModal;
